import type {
	BoundActions,
	HookActionsFunction,
	HookFunction,
	Store,
	StoreActionApi,
} from 'react-sweet-state';
import { createStore, createHook, createActionsHook } from 'react-sweet-state';

import type { MarkdownConvertDirection } from '@atlassian/editor-plugin-markdown-mode/markdownModePluginType';

/**
 * Shared open/close state for the markdown-mode conversion modals.
 *
 * `dialog` is the single source of truth for the rich-text ↔ markdown
 * confirmation modal. For `toMarkdown`, `isLossy` and `syncBlockCount`
 * drive warning treatment. For `toRichText`, both are forced to `false`/`0`.
 *
 * State is lifted out of `useMarkdownModeActions` so the three entry
 * points (header overflow menu, title `.md`-suffix gesture, `cmd+shift+x`
 * shortcut) drive the same dialog instances, which are rendered exactly
 * once (currently in `MoreActions`). Single global flag is fine because
 * only one Live page editor is mounted at a time. If that ever changes,
 * key by `contentId`.
 */
type ConvertDialogClosed = { isOpen: false };
type ConvertDialogOpen = {
	isOpen: true;
	direction: MarkdownConvertDirection;
	isLossy: boolean;
	syncBlockCount: number;
};
export type ConvertDialogStateValue = ConvertDialogClosed | ConvertDialogOpen;

type ConvertDialogState = {
	dialog: ConvertDialogStateValue;
};

const initialState: ConvertDialogState = {
	dialog: { isOpen: false },
};

export type OpenConvertDialogPayload = {
	direction: MarkdownConvertDirection;
	/** Only meaningful for `toMarkdown`. Ignored for `toRichText` (forced to `false`). */
	isLossy?: boolean;
	/** Only meaningful for `toMarkdown`. Number of synced blocks found in the doc. */
	syncBlockCount?: number;
};

export const ConvertDialogStore: Store<
	ConvertDialogState,
	{
		openConvertDialog: (
			payload: OpenConvertDialogPayload,
		) => ({ setState, getState }: StoreActionApi<ConvertDialogState>) => void;
		closeConvertDialog: () => ({ setState, getState }: StoreActionApi<ConvertDialogState>) => void;
	}
> = createStore({
	initialState,
	actions: {
		openConvertDialog:
			(
				payload: OpenConvertDialogPayload,
			): (({ setState, getState }: StoreActionApi<ConvertDialogState>) => void) =>
			({ setState, getState }) => {
				const next: ConvertDialogStateValue = {
					isOpen: true,
					direction: payload.direction,
					isLossy: payload.direction === 'toMarkdown' ? Boolean(payload.isLossy) : false,
					syncBlockCount:
						payload.direction === 'toMarkdown' ? Math.max(payload.syncBlockCount ?? 0, 0) : 0,
				};
				const current = getState();
				if (
					current.dialog.isOpen &&
					current.dialog.direction === next.direction &&
					current.dialog.isLossy === next.isLossy &&
					current.dialog.syncBlockCount === next.syncBlockCount
				) {
					return;
				}
				setState({ dialog: next });
			},
		closeConvertDialog:
			(): (({ setState, getState }: StoreActionApi<ConvertDialogState>) => void) =>
			({ setState, getState }) => {
				if (getState().dialog.isOpen) {
					setState({ dialog: { isOpen: false } });
				}
			},
	},
	name: 'ConvertDialogStore',
});

export const useConvertDialogState: HookFunction<
	ConvertDialogState,
	BoundActions<
		ConvertDialogState,
		{
			openConvertDialog: (
				payload: OpenConvertDialogPayload,
			) => ({ setState, getState }: StoreActionApi<ConvertDialogState>) => void;
			closeConvertDialog: () => ({
				setState,
				getState,
			}: StoreActionApi<ConvertDialogState>) => void;
		}
	>,
	void
> = createHook(ConvertDialogStore);
export const useConvertDialogActions: HookActionsFunction<
	BoundActions<
		ConvertDialogState,
		{
			openConvertDialog: (
				payload: OpenConvertDialogPayload,
			) => ({ setState, getState }: StoreActionApi<ConvertDialogState>) => void;
			closeConvertDialog: () => ({
				setState,
				getState,
			}: StoreActionApi<ConvertDialogState>) => void;
		}
	>
> = createActionsHook(ConvertDialogStore);
